import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ArrowRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useNotifications } from '../contexts/NotificationsContext';
import { NotificationItem } from './ui/NotificationItem';
import { useTranslation } from 'react-i18next';

export function NotificationBell({ side = 'bottom', align = 'end', className = '' } = {}) {
  const { t } = useTranslation();
  const { notifications, unreadCount, markRead, markAllRead, deleteNotification } = useNotifications();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleNavigate = (url) => {
    setOpen(false);
    navigate(url);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative p-2 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground ${className}`}
          aria-label={t('notifications.title')}
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 text-[10px] font-bold leading-4 text-center rounded-full bg-red-500 text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 max-h-[480px] flex flex-col bg-popover border border-border rounded-xl shadow-xl"
        side={side}
        align={align}
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-sm text-foreground">{t('notifications.title')}</h3>
          {unreadCount > 0 && (
            <button
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={markAllRead}
            >
              <CheckCheck size={12} />
              {t('notifications.mark_all_read')}
            </button>
          )}
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <Bell size={32} className="mb-2 opacity-30" />
              <p className="text-sm">{t('notifications.no_notifications')}</p>
            </div>
          ) : (
            <div className="p-2 space-y-0.5">
              {notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onMarkRead={markRead}
                  onDelete={deleteNotification}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-border p-2">
          <Link
            to="/app/notifications"
            className="flex items-center justify-center gap-1.5 w-full px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-accent"
          >
            {t('notifications.see_all')}
            <ArrowRight size={12} />
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default NotificationBell;
